import { Column, Row } from '@simplywallst/ui-core';
import DividendStats from './components/DividendStats';
import { shallowEqual, useSelector } from 'react-redux';
import Media from 'react-media';
import { ColumnWithSpacing } from './styled';
import {
  Question,
  Link,
  ChevronRightIcon,
  HorizontalRule,
} from '@/pages/CompanyReport/components/Overview/components/Fundamentals/styled';
import { SubSubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSubSectionHeader';
import { useTheme } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import selector from './selector';
import { COMPANY_DIVIDEND_YIELD_AND_PAYMENT } from '@/constants/feedbackSections';
import NextDividendTable from './components/NextDividendTable';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useCompanyReportPageUrl } from '@/pages/CompanyReport/hooks/useCompanyReportPageUrl';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

const NextDividendChart = loadable(
  () => import('./components/NextDividendChart'),
  { resolveComponent: (component) => component.NextDividendChart }
);

const ChartWrapper = loadable(
  () =>
    import('@/pages/CompanyReport/components/Overview/components/ChartWrapper')
);

const Dividends = () => {
  const theme = useTheme();
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const companyUrl = useCompanyReportPageUrl();
  const { loadSection } = useVisibleSectionsContext();

  const t = useOverviewTranslation();

  if (!selected) return null;
  if (!selected.dividendYield) return null;

  const showChart = Boolean(
    selected.dividendNextDate && selected.dividendNextPayDate
  );
  const handleLinkClick = () => {
    loadSection('dividend');
  };

  return (
    <>
      <HorizontalRule />
      <Row data-cy-id="overview-fundamentals-dividends">
        <ColumnWithSpacing colMd={6} colSm={12}>
          <SubSubSectionHeader>
            {t('fundamentals.dividend.chart.heading')}
          </SubSubSectionHeader>
          <DividendStats
            dividendYield={selected.dividendYieldFormatted}
            payoutRatio={selected.currentDividendPayoutRatioFormatted}
          />
          <Media query={{ minWidth: theme.mdMin }}>
            {(match) =>
              (showChart ? match : !match) && (
                <div>
                  <Question>
                    {t('fundamentals.dividend.chart.doesPayReliableDividends', {
                      tickerSymbol: selected.tickerSymbol,
                    })}
                  </Question>
                  <Link
                    data-cy-id="overview-fundamentals-dividends-link"
                    to={`${companyUrl}/dividend`}
                    onClick={handleLinkClick}
                  >
                    {t('fundamentals.dividend.chart.historyLink', {
                      tickerSymbol: selected.tickerSymbol,
                    })}
                    <ChevronRightIcon />
                  </Link>
                </div>
              )
            }
          </Media>
        </ColumnWithSpacing>
        <Column
          colMd={6}
          colSm={12}
          displayFlex
          column
          justifyContent="flex-end"
        >
          {showChart && (
            <ChartWrapper
              id="overview-income-statement"
              chart={
                <NextDividendChart
                  dividendNextDate={selected.dividendNextDate}
                  dividendNextPayDate={selected.dividendNextPayDate}
                />
              }
              table={<NextDividendTable />}
              preRenderHeight={200}
              actionsMarginTop={16}
              feedbackLabel={COMPANY_DIVIDEND_YIELD_AND_PAYMENT}
            />
          )}
          <Media query={{ maxWidth: theme.smMax }}>
            {(match) =>
              (showChart ? match : !match) && (
                <div>
                  <Question>
                    {t('fundamentals.dividend.chart.doesPayReliableDividends', {
                      tickerSymbol: selected.tickerSymbol,
                    })}
                  </Question>
                  <Link
                    data-cy-id="overview-fundamentals-dividends-link"
                    to={`${companyUrl}/dividend`}
                    onClick={handleLinkClick}
                  >
                    {t('fundamentals.dividend.chart.historyLink', {
                      tickerSymbol: selected.tickerSymbol,
                    })}
                    <ChevronRightIcon />
                  </Link>
                </div>
              )
            }
          </Media>
        </Column>
      </Row>
    </>
  );
};
export default Dividends;
